import { UserRole } from './enums';

/**
 * The permission matrix — JP-CRM Area 1 §4.3, transcribed one row per capability.
 *
 * This is the ONLY place that says which role may do what. The API gates routes with
 * `@Can('<capability>')`, services ask `can(role, …)` / `moneyScope(role)`, and the web
 * derives navigation and visible controls from the same table. Nothing else may hold a
 * list of roles.
 *
 * Matrix version: Area 1 v2.0 (12 Oct 2026).
 *
 * The table below is the DEFAULT. A Super Admin may override single cells from
 * Settings → Permissions; the API loads those overrides at boot (and after each save)
 * and the web fetches them at sign-in, both through `applyPermissionOverrides()`. Every
 * query helper reads the EFFECTIVE matrix (default + overrides).
 *
 * Levels (the document's legend):
 *   F  full
 *   W  write within own scope
 *   R  read, including amounts
 *   U  read, units and status only — no amounts
 *   O  own records only
 *   —  no access
 */
export type PermissionLevel = 'F' | 'W' | 'R' | 'U' | 'O' | '-';

export const PERMISSION_LEVELS: readonly PermissionLevel[] = ['F', 'W', 'R', 'U', 'O', '-'];

/** Column order of every row below — keep in sync with ROLE_COLUMNS. */
const ROLE_COLUMNS = [
  UserRole.SUPER_ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.BUSINESS_MANAGER,
  UserRole.SALES_MANAGER,
  UserRole.PARTNER,
  UserRole.ASSISTANT,
  UserRole.WAREHOUSE,
  UserRole.AGENT,
] as const;

type Row = readonly [
  PermissionLevel, // SUPER_ADMIN
  PermissionLevel, // ACCOUNTANT
  PermissionLevel, // BUSINESS_MANAGER
  PermissionLevel, // SALES_MANAGER
  PermissionLevel, // PARTNER
  PermissionLevel, // ASSISTANT
  PermissionLevel, // WAREHOUSE
  PermissionLevel, // AGENT
];

/* prettier-ignore */
const MATRIX = {
  //                                 SA   ACC  BM   SM   PART ASST WH   AGENT
  'dashboard.financial':           ['F', '-', '-', '-', '-', '-', '-', '-'],
  'dashboard.financePage':         ['F', '-', '-', '-', '-', '-', '-', '-'],
  'dashboard.volume':              ['F', 'U', 'U', 'U', 'U', 'U', '-', 'O'],
  'dashboard.warehouse':           ['F', '-', 'R', '-', '-', 'R', 'F', '-'],

  'orders.list':                   ['F', 'U', 'U', 'U', 'U', 'U', '-', 'O'],
  'orders.create':                 ['F', '-', '-', 'W', '-', 'W', '-', 'W'],
  'orders.editBeforeProforma':     ['F', '-', 'W', 'W', '-', 'W', '-', 'O'],
  'orders.manualStatus':           ['F', '-', 'W', '-', '-', '-', '-', '-'],
  'orders.cancelArchive':          ['F', '-', 'W', 'W', '-', '-', '-', 'O'],
  'orders.value':                  ['F', '-', '-', '-', '-', '-', '-', 'O'],
  'orders.releaseCommercial':      ['F', '-', '-', 'F', '-', '-', '-', '-'],
  'orders.releaseFinancial':       ['F', '-', '-', '-', '-', '-', '-', '-'],

  'clients.view':                  ['F', 'U', 'U', 'U', 'U', 'U', '-', 'O'],
  'clients.write':                 ['F', '-', 'W', 'W', '-', 'W', '-', 'O'],
  'clients.reassign':              ['F', '-', '-', 'W', '-', '-', '-', '-'],
  'clients.deleteAnonymise':       ['F', '-', '-', '-', '-', '-', '-', '-'],
  'clients.merge':                 ['F', '-', 'W', '-', '-', '-', '-', '-'],

  'tags.manage':                   ['F', '-', '-', '-', '-', '-', '-', '-'],
  'tags.apply':                    ['F', '-', 'W', 'W', '-', 'W', '-', 'O'],

  'products.view':                 ['F', 'R', 'U', 'U', 'U', 'U', 'R', 'R'],
  'products.prices':               ['F', '-', '-', '-', '-', 'R', '-', 'R'],
  'products.edit':                 ['F', '-', '-', '-', '-', '-', '-', '-'],

  'stock.view':                    ['F', 'R', 'F', 'U', 'U', 'R', 'F', '-'],
  'stock.goodsIn':                 ['F', '-', 'W', '-', '-', 'W', 'W', '-'],
  'stock.shortfall':               ['F', '-', 'F', 'F', '-', 'R', 'R', '-'],
  'stock.pick':                    ['F', '-', 'F', '-', '-', 'W', 'F', '-'],

  'payments.view':                 ['F', '-', '-', 'F', '-', '-', '-', '-'],
  'payments.reconcile':            ['F', '-', '-', 'F', '-', '-', '-', '-'],
  'mandates.view':                 ['F', 'F', '-', 'R', '-', 'R', '-', '-'],
  'mandates.activate':             ['F', 'F', '-', 'F', '-', '-', '-', '-'],
  'documents.view':                ['F', 'U', 'U', '-', '-', 'U', '-', 'O'],
  'creditNotes.raise':             ['F', '-', '-', '-', '-', '-', '-', '-'],
  'returns.record':                ['F', 'U', 'F', 'W', '-', 'W', 'W', 'O'],

  'agents.list':                   ['F', 'U', 'U', 'F', 'U', '-', '-', '-'],
  'agents.manage':                 ['F', '-', '-', 'W', '-', '-', '-', '-'],
  'commission.own':                ['F', '-', '-', '-', '-', '-', '-', 'O'],
  'commission.all':                ['F', '-', '-', '-', '-', '-', '-', '-'],
  'commission.settle':             ['F', '-', '-', '-', '-', '-', '-', '-'],

  'reports.moneyOps':              ['F', '-', '-', '-', '-', '-', '-', '-'],
  'reports.salesEuros':            ['F', '-', '-', '-', '-', '-', '-', '-'],
  'reports.volume':                ['F', 'F', 'F', 'F', 'R', 'R', '-', 'O'],
  'exports.withAmounts':           ['F', '-', '-', '-', '-', '-', '-', 'O'],
  'exports.withoutAmounts':        ['F', 'F', 'F', 'F', 'R', 'R', '-', 'O'],

  'calendar.view':                 ['F', 'R', 'F', 'F', 'R', 'F', '-', 'R'],
  'calendar.write':                ['F', '-', 'W', 'W', '-', 'W', '-', 'O'],

  'users.manage':                  ['F', '-', '-', '-', '-', '-', '-', '-'],
  'settings.manage':               ['F', '-', '-', '-', '-', '-', '-', '-'],
  'audit.view':                    ['F', 'R', 'R', '-', '-', '-', '-', 'O'],
} as const satisfies Record<string, Row>;

export type Capability = keyof typeof MATRIX;

/** Every capability name (for tests / tooling), in matrix order. */
export const CAPABILITIES = Object.keys(MATRIX) as readonly Capability[];

/** Every role, in the matrix's column order. */
export const ALL_ROLES: readonly UserRole[] = ROLE_COLUMNS;

/**
 * The capabilities that gate something a user can actually reach TODAY (a route, a
 * menu entry, a button, a column). The Settings → Permissions picker shows only these;
 * the rest of the matrix stays as the document default for modules not built yet
 * (stock, mandates, commission, calendar, tags, approvals…) and joins this list the day
 * its screen ships. Keep in sync when a new @Can / can() reference lands.
 */
export const LIVE_CAPABILITIES: readonly Capability[] = [
  // Agents screen: list/performance vs invite/block/office.
  'agents.list',
  'agents.manage',
  // Administration.
  'users.manage',
  'settings.manage',
  // Returns to the list when their module is switched on again (web modules.ts):
  // 'dashboard.volume', 'dashboard.financePage', 'orders.list', 'orders.create',
  // 'orders.manualStatus', 'orders.cancelArchive', 'orders.value', 'clients.view',
  // 'clients.write', 'clients.reassign', 'clients.deleteAnonymise', 'products.view',
  // 'products.prices', 'reports.moneyOps', 'reports.salesEuros' (agents CSV export).
];

/** Capability groups for the Settings → Permissions screen (section = key prefix). */
export const CAPABILITY_GROUPS: ReadonlyArray<{ key: string; capabilities: readonly Capability[] }> = (() => {
  const groups = new Map<string, Capability[]>();
  for (const cap of CAPABILITIES) {
    const section = cap.split('.')[0];
    const list = groups.get(section) ?? [];
    list.push(cap);
    groups.set(section, list);
  }
  return Array.from(groups, ([key, capabilities]) => ({ key, capabilities }));
})();

// ── Effective matrix: defaults + Super Admin overrides ─────────────────────────────

export interface PermissionOverride {
  readonly capability: Capability;
  readonly role: UserRole;
  readonly level: PermissionLevel;
}

type Cell = `${Capability}:${UserRole}`;
const cellKey = (cap: Capability, role: UserRole): Cell => `${cap}:${role}`;

/** Current overrides, keyed by cell. Replaced wholesale by applyPermissionOverrides(). */
let overrides: ReadonlyMap<Cell, PermissionLevel> = new Map();

/**
 * Replace the active overrides (the API calls this at boot and after every save; the
 * web after sign-in). Pass [] to return to the document defaults.
 */
export function applyPermissionOverrides(list: readonly PermissionOverride[]): void {
  overrides = new Map(list.map((o) => [cellKey(o.capability, o.role), o.level]));
}

/** The overrides currently applied (for display / diffing). */
export function activePermissionOverrides(): PermissionOverride[] {
  return Array.from(overrides, ([key, level]) => {
    const [capability, role] = key.split(':') as [Capability, UserRole];
    return { capability, role, level };
  });
}

/** The DOCUMENT default for a cell, ignoring overrides. */
export function defaultCapabilityLevel(role: UserRole, cap: Capability): PermissionLevel {
  const idx = ROLE_COLUMNS.indexOf(role);
  if (idx === -1) return '-';
  return MATRIX[cap][idx];
}

/** The EFFECTIVE level a role holds for a capability ('-' = no access). */
export function capabilityLevel(role: UserRole, cap: Capability): PermissionLevel {
  return overrides.get(cellKey(cap, role)) ?? defaultCapabilityLevel(role, cap);
}

/** True when the role has ANY level of access to the capability. */
export function can(role: UserRole, cap: Capability): boolean {
  return capabilityLevel(role, cap) !== '-';
}

/**
 * True when the role holds the capability at one of the given levels — for actions
 * where "read units only" (U) or "own records" (O) must not suffice, e.g. re-sending a
 * fiscal document to a client requires documents.view at F or R.
 */
export function hasLevel(
  role: UserRole,
  cap: Capability,
  levels: readonly PermissionLevel[],
): boolean {
  return levels.includes(capabilityLevel(role, cap));
}

/** The roles that hold any level of access to a capability (for @Can + nav). */
export function rolesWith(cap: Capability): readonly UserRole[] {
  return ROLE_COLUMNS.filter((r) => can(r, cap));
}

/**
 * Financial visibility — principle R1 (Area 1 §5), derived from the `orders.value`
 * row so one cell of the matrix switches money on or off for a role:
 *   ALL  — F or R on orders.value (Super Admin, Accountant by default),
 *   OWN  — O (the Agent: own orders and commission only),
 *   NONE — everyone else (units, status and activity only).
 */
export type MoneyScope = 'ALL' | 'OWN' | 'NONE';

export function moneyScope(role: UserRole): MoneyScope {
  switch (capabilityLevel(role, 'orders.value')) {
    case 'F':
    case 'R':
    case 'W':
    case 'U':
      return 'ALL';
    case 'O':
      return 'OWN';
    default:
      return 'NONE';
  }
}

/** True for roles that see money on any record they may open. */
export function seesAllMoney(role: UserRole): boolean {
  return moneyScope(role) === 'ALL';
}

/**
 * "Staff" = every role except the Agent: they work in the admin portal and their data
 * queries are not scoped to an owner. (What they may open is still decided per
 * capability; this only says whether the owner filter applies.)
 */
export function isStaff(role: UserRole): boolean {
  return role !== UserRole.AGENT;
}

/** Roles that enter the admin portal (/admin). The Agent has their own portal. */
export const ADMIN_PORTAL_ROLES: readonly UserRole[] = ROLE_COLUMNS.filter(isStaff);

/**
 * User administration, derived from the matrix: `users.manage` (F) manages every role;
 * `agents.manage` (F/W) manages Agents only.
 */
export function manageableRoles(actor: UserRole): readonly UserRole[] {
  if (capabilityLevel(actor, 'users.manage') === 'F') return ROLE_COLUMNS;
  if (hasLevel(actor, 'agents.manage', ['F', 'W'])) return [UserRole.AGENT];
  return [];
}

export function canManageRole(actor: UserRole, target: UserRole): boolean {
  return manageableRoles(actor).includes(target);
}

// ── Guard-rails for editing the matrix ─────────────────────────────────────────────

/** Capabilities only the Super Admin may ever hold (handing them out locks the system). */
export const SUPER_ADMIN_ONLY_CAPABILITIES: readonly Capability[] = ['users.manage', 'settings.manage'];

/**
 * Financial figures — revenue, turnover, order and invoice amounts, payments, commissions,
 * exports with amounts — are the Super Admin's only; every other role works in orders,
 * units and statuses. Two agreed exceptions: the Agent keeps their OWN order amounts and
 * commission; the Sales Manager sees amounts in the payment queue only (payments.*).
 * Enforced as a guard-rail on overrides too.
 */
export const MONEY_CAPABILITIES: readonly Capability[] = [
  'dashboard.financial',
  'dashboard.financePage',
  'orders.value',
  'payments.view',
  'payments.reconcile',
  'creditNotes.raise',
  'commission.own',
  'commission.all',
  'commission.settle',
  'reports.moneyOps',
  'reports.salesEuros',
  'exports.withAmounts',
];

/**
 * Who releases which approval hold is fixed (commercial holds → Sales Manager, financial
 * holds → Super Admin); thresholds are configurable, the releasing role is not.
 */
export const FIXED_CAPABILITIES: readonly Capability[] = ['orders.releaseCommercial', 'orders.releaseFinancial'];

/**
 * Sales & performance money (revenue per agent, lifetime value, average order, KPI
 * tiles in euros) is the Super Admins' view alone; the Agent sees their own figures.
 */
export function seesPerformanceMoney(role: UserRole): boolean {
  return can(role, 'reports.salesEuros') || moneyScope(role) === 'OWN';
}

/**
 * Why an override is refused, or null when it is acceptable:
 *  - the Super Admin column is fixed at F (otherwise an admin could lock themselves out);
 *  - users.manage / settings.manage stay Super Admin only;
 *  - the Agent is owner-scoped at the data layer: a cell may be '-', 'O', or its default
 *    (never a staff-wide F/W/R/U that the scoping would silently contradict).
 */
export function validatePermissionOverride(o: PermissionOverride): string | null {
  if (!CAPABILITIES.includes(o.capability)) return `Unknown capability ${o.capability}`;
  if (!ROLE_COLUMNS.includes(o.role)) return `Unknown role ${o.role}`;
  if (!PERMISSION_LEVELS.includes(o.level)) return `Unknown level ${o.level}`;
  if (o.role === UserRole.SUPER_ADMIN && o.level !== 'F') {
    return 'The Super Admin column cannot be changed';
  }
  if (SUPER_ADMIN_ONLY_CAPABILITIES.includes(o.capability) && o.role !== UserRole.SUPER_ADMIN && o.level !== '-') {
    return `${o.capability} is reserved to the Super Admin`;
  }
  if (FIXED_CAPABILITIES.includes(o.capability) && o.level !== defaultCapabilityLevel(o.role, o.capability)) {
    return `${o.capability}: the releasing role is fixed and cannot be changed`;
  }
  if (MONEY_CAPABILITIES.includes(o.capability) && o.role !== UserRole.SUPER_ADMIN) {
    // The matrix default is always allowed back (e.g. the Sales Manager's payment queue).
    const base: readonly PermissionLevel[] = o.role === UserRole.AGENT ? ['-', 'O'] : ['-', 'U'];
    const allowed: readonly PermissionLevel[] = [...base, defaultCapabilityLevel(o.role, o.capability)];
    if (!allowed.includes(o.level)) {
      return `${o.capability}: financial figures are visible to the Super Admin only`;
    }
  }
  if (o.role === UserRole.AGENT) {
    const def = defaultCapabilityLevel(UserRole.AGENT, o.capability);
    if (o.level !== '-' && o.level !== 'O' && o.level !== def) {
      return 'An Agent may only hold a capability for their own records (O) or not at all';
    }
  }
  return null;
}
